import React from "react";

export default function Pagination({
  currentPage,
  totalPages,
  isLastPage,
  onPageChange,
  totalItems,
  itemsPerPage = 10,
  showInfo = true,
}) {
  // Calculate display range
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems || currentPage * itemsPerPage);

  // Determine if we're on the last page
  const isOnLastPage = totalPages 
    ? currentPage >= totalPages 
    : isLastPage;

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const effectiveTotalPages = totalPages || (isLastPage ? currentPage : currentPage + 1);

    if (effectiveTotalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= effectiveTotalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination with ellipsis
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(effectiveTotalPages);
      } else if (currentPage >= effectiveTotalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = effectiveTotalPages - 3; i <= effectiveTotalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(effectiveTotalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4">
      {/* Info */}
      {showInfo && (
        <p className="text-sm text-gray-500 order-2 sm:order-1">
          Showing page <span className="font-semibold text-gray-700">{currentPage}</span>
          {totalPages && (
            <span> of <span className="font-semibold text-gray-700">{totalPages}</span></span>
          )}
          {totalItems > 0 && (
            <>
              {" "}
              ({startItem}-{endItem} of{" "}
              <span className="font-semibold text-gray-700">{totalItems}</span> results)
            </>
          )}
        </p>
      )}

      {/* Controls */}
      <div className="flex items-center gap-2 order-1 sm:order-2">
        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium 
            bg-white border border-gray-200 text-gray-700 
            hover:bg-gray-50 hover:border-gray-300
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200
            transition-all shadow-sm"
        >
          <i className="ri-arrow-left-s-line"></i>
          <span className="hidden sm:inline">Previous</span>
        </button>

        {/* Page Numbers */}
        <div className="hidden sm:flex items-center gap-1">
          {pageNumbers.map((page, idx) => {
            if (page === "...") {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 py-2 text-gray-400 text-sm"
                >
                  •••
                </span>
              );
            }

            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                    : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>

        {/* Mobile Current Page */}
        <span className="sm:hidden px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-sm">
          {currentPage}
        </span>

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isOnLastPage}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium 
            bg-white border border-gray-200 text-gray-700 
            hover:bg-gray-50 hover:border-gray-300
            disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200
            transition-all shadow-sm"
        >
          <span className="hidden sm:inline">Next</span>
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>
  );
}
